import { graphql } from 'gatsby';
import React from 'react';
import { css } from '@emotion/react';
import { useMicrosite } from '../../../contexts/franchise/microsite-context';
import { colors } from '../../primitives/colors';
import Link from '../../primitives/link';
import SplitHero from '../hero/split-hero';
import { breakpoints } from '../../primitives/tokens';

const styles = {
    wrap: css({
        marginTop: '20px',
        color: colors.grey800,
    }),
    marginTop: css({
        marginTop: '16px',
    }),
    heading: css({
        fontSize: '20px',
        lineHeight: '28px',
        fontWeight: 500,
        color: colors.primaryOrange,
        [breakpoints.tb]: {
            fontSize: '18px',
            lineHeight: '28px',
        },
    }),
    serviceArea: css({
        fontSize: '20px',
        lineHeight: '28px',
        [breakpoints.tb]: {
            fontSize: '18px',
            lineHeight: '28px',
        },
    }),
    hyperlocalLink: css({
        textDecoration: 'underline',
        transition: '0.3s',
        '&:hover': {
            color: colors.primaryGreen,
        },
    }),
};

const FranchiseContactHero: React.FC<Queries.FranchiseContactHeroDataFragment> = (props): JSX.Element | null => {
    const franchiseData = useMicrosite();
    const { image } = props;

    if (!franchiseData.franchise) {
        return null;
    }

    const {
        name,
        address,
        activeMarkets,
        addressHidden,
    } = franchiseData.franchise;

    if (!address) {
        return null;
    }

    const { address: line1, addressExt: line2, city, state, postalCode } = address;

    return (
        <SplitHero heading={name} image={image}>
            <div css={styles.wrap}>
                {!addressHidden && (
                    <address css={styles.marginTop}>
                        <h4 css={styles.heading}>Address</h4>
                        {!!line1 && (
                            <>
                                {line1}
                                <br />
                            </>
                        )}
                        {!!line2 && (
                            <>
                                {line2}
                                <br />
                            </>
                        )}
                        {city}
                        ,
                        {state}
                        {' '}
                        {postalCode}
                    </address>
                )}
                {!!activeMarkets && activeMarkets.length > 0 && (
                    <div css={styles.marginTop}>
                        <h4 css={styles.heading}>Service Area</h4>
                        <div css={styles.serviceArea}>
                            {activeMarkets.map((activeMarket, idx) => {
                                const place = `${activeMarket.city}, ${activeMarket.state}`;
                                const isLast = idx === activeMarkets.length - 1;

                                return activeMarket.link ? (
                                    <span key={place}>
                                        <Link css={styles.hyperlocalLink} to={activeMarket.link}>
                                            {place}
                                        </Link>
                                        {!isLast && ' | '}
                                    </span>
                                ) : (
                                    <span key={place}>
                                        {place}
                                        {!isLast && ' | '}
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </SplitHero>
    );
};

export const query = graphql`
  fragment FranchiseContactHeroData on CsFranchiseContactHeroGroup {
    image {
        filename
        title
        description
        url
        contentStackAsset(width: 1440)
    }
  }
`;

export default FranchiseContactHero;
